import React from 'react';
import { useState, useEffect, useContext } from 'react';
import { AppContext } from "../../utils/ContextProvider";
import Loader from "../../components/Loader";
import { useLocation, Link, useNavigate } from 'react-router-dom';
import './style.css'
import API from '../../utils/API';
import Swal from 'sweetalert2';
import GlobalSVG from '../../utils/GlobalSVG';
import SlideToggle from '../../components/SlideToggle';
import { uploadAndScan } from '../../utils/utils';

const Client = () => {

    const location = useLocation();
    const [client, setClient] = useState(null)
    const [getPutPost, setGetPutPost] = useState()
    const [clientData, setClientData] = useState({ client_id: "", client_name: "", logo: "", address: "", address2: "", city: "", province: "", postalcode: "", country: "" })
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const context = useContext(AppContext)
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    useEffect(() => {
        if (!context.getUserInfo) return
        let access = ["access_clients_list", "create_clients", "update_clients"]
        if (!access.some(item => context.getUserInfo[item] === true)) {
            navigate('/settings/users')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context.getUserInfo])

    useEffect(() => {
        if (getPutPost === 'put') return
        if (location.pathname === '/client/create') {
            setGetPutPost('post')
        } else if (queryParams.size !== 0) {
            setClient(queryParams.get('client'))
            setGetPutPost('get')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams, location])

    useEffect(() => {
        if (getPutPost === 'get' && client !== null) {
            setLoading(true)
            const cancelToken = API.cancelToken();

            API.getClient(cancelToken, client).then(res => {
                setClientData(res.data)
                return
            }).catch(err => {
                console.log(err)
                if (err.message === 'cancelling') return
                if (err.response && err.response.data) {
                    try {
                        if (err.response.data.includes('error')) {
                            Swal.fire({
                                title: 'Error.',
                                text: err.response.data.error,
                                icon: 'warning',
                                confirmButtonText: 'Ok',
                                confirmButtonColor: '#007b4e',
                            });
                        }
                    } catch (e) {
                        Swal.fire({
                            title: 'Error.',
                            text: err.response.data,
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                            confirmButtonColor: '#007b4e',
                        });
                    }
                } else {
                    Swal.fire({
                        title: 'Error.',
                        text: 'An error occurred, please try again later.',
                        icon: 'warning',
                        confirmButtonText: 'Ok',
                        confirmButtonColor: '#007b4e',
                    });
                }
            }).finally(f => {
                setLoading(false)
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getPutPost, client])

    const updateClient = async (e) => {
        const { name, value, files } = e.target;
        if (name === 'logo') {
            const selectedImage = await Promise.all(
                Array.from(files).map(file =>
                    new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve({
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            data: e.target.result
                        });
                        reader.readAsDataURL(file);
                    })
                )
            );
            setClientData(prevValue => {
                return {
                    ...prevValue,
                    logo: selectedImage
                }
            })            
        } else {
            setClientData(prevValue => {
                return {
                    ...prevValue,
                    [name]: value.trim()
                }
            })
        }
    }

    const FIELDS_TO_REMOVE = ['created_at', 'updated_at'];

    const showErrorAlert = (message) => {
        Swal.fire({
            title: 'Error.',
            text: message,
            icon: 'warning',
            confirmButtonText: 'Ok',
            confirmButtonColor: '#007b4e',
        });
    };

    const sanitizePayload = (data, isPost) => {
        const payload = JSON.parse(JSON.stringify(data));

        FIELDS_TO_REMOVE.forEach((field) => delete payload[field]);
        if (!isPost) delete payload['client_id'];

        for (const [key, value] of Object.entries(payload)) {
            if (value === null || String(value).trim() === '') {
                delete payload[key];
            }
        }

        return payload;
    };

    const resolveLogoField = async (payload) => {
        if (!payload.logo || payload.logo.includes('https')) {
            delete payload.logo;
            return true;
        }

        const id = await uploadAndScan(payload.logo, "logo");

        if (id.length) payload.logo = id[0]
        else {
            delete payload.logo
            return false
        }

        return true

    };

    const postClient = async () => {
        if (!clientData.client_name) {
            setError({ client_name: 'Missing Value' });
            return;
        }

        const isPost = getPutPost === 'post';
        const payload = sanitizePayload(clientData, isPost);
        const cancelToken = API.cancelToken();

        setLoading(true);

        const logoResolved = await resolveLogoField(payload);
        if (!logoResolved) return;

        try {
            if (isPost) {
                await API.postClient(cancelToken, payload);
                navigate(`/client?client=${clientData.client_id}`);
            } else {
                await API.putClient(cancelToken, client, payload);
                setGetPutPost('get');
                setError(null);
            }
        } catch (err) {
            if (err.message === 'cancelling') return;

            const errorMessage =
                err.response?.data?.error ??
                (typeof err.response?.data === 'string' ? err.response.data : null) ??
                'An error occurred, please try again later.';

            setError(err.response?.data ?? null);
            showErrorAlert(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className='tablet:p-8 mobile:p-3 min-h-[80vh]'>
            <div className='flex flex-nowrap items-center gap-4 tablet:mb-8 mobile:mb-3 text-base'>
                <Link to="/settings/clients">
                    Clients
                </Link>
                {
                    GlobalSVG.rightArrow()
                }
                <p className='font-medium'>
                    {
                        ['get', 'put'].includes(getPutPost) ? clientData.client_name : "Add Client"

                    }
                </p>
            </div>
            <div className='card tablet:p-8 mobile:p-3 min-h-[70vh]'>
                <div className="input-border flex flex-wrap items-center justify-between text-3xl p-2 w-full">
                    <div className='items-center mb-6 laptop:w-1/2 tablet:w-full mobile:'>
                        <input
                            placeholder='Client Name'
                            className='text-3xl p-2 outline-none hide-input-background laptop:w-1/2 tablet:w-full mobile:w-full'
                            type="text"
                            name='client_name'
                            disabled={getPutPost === 'get'}
                            defaultValue={clientData.client_name ? clientData.client_name : ""}
                            onChange={updateClient} />
                        {
                            error && error.client_name &&
                            <p className='text-red text-xs p-2'>{error.client_name}</p>

                        }
                    </div>
                    <div className="flex flex-nowrap items-center justify-center mb-6">
                        <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color mt-2' htmlFor="active">Active</label>
                        <SlideToggle disabled={getPutPost === 'get'} setOptions={setClientData} options={clientData} id={"active"} />
                    </div>
                </div>
                {
                    loading &&
                    <Loader />
                }
                <div className='flex flex-wrap'>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="client_id">Client ID</label>
                            <input
                                placeholder='Client ID'
                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                type="text"
                                name="client_id" defaultValue={clientData.client_id ? clientData.client_id : ""}
                                disabled={getPutPost === 'get'}
                                onChange={updateClient} />
                            {
                                error && error.client_id &&
                                <p className='text-red text-xs'>{error.client_id}</p>

                            }
                        </div>
                    </div>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="logo">Client Logo</label>
                            {
                                getPutPost === 'get' &&
                                <img className='w-20' src={clientData.logo ? clientData.logo : require('../../assets/images/placeholder-image.png')} alt="" />
                            }
                            {getPutPost !== 'get' &&
                                <input
                                    className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                    type="file"
                                    name="logo"
                                    onChange={updateClient} />
                            }
                        </div>
                    </div>
                </div>
                <div className='flex flex-wrap'>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="address">Address</label>
                            <input
                                placeholder='Address'
                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                type="text"
                                name="address" defaultValue={clientData.address ? clientData.address : ""}
                                disabled={getPutPost === 'get'}
                                onChange={updateClient} />
                            {
                                error && error.address &&
                                <p className='text-red text-xs'>{error.address}</p>

                            }
                        </div>
                    </div>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="address2">Address 2</label>
                            <input
                                placeholder='Address 2'
                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                type="text"
                                name="address2" defaultValue={clientData.address2 ? clientData.address2 : ""}
                                disabled={getPutPost === 'get'}
                                onChange={updateClient} />
                            {
                                error && error.address2 &&
                                <p className='text-red text-xs'>{error.address2}</p>

                            }
                        </div>
                    </div>
                </div>
                <div className='flex flex-wrap'>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="city">City</label>
                            <input
                                placeholder='City'
                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                type="text"
                                name="city" defaultValue={clientData.city ? clientData.city : ""}
                                disabled={getPutPost === 'get'}
                                onChange={updateClient} />
                            {
                                error && error.city &&
                                <p className='text-red text-xs'>{error.city}</p>

                            }
                        </div>
                    </div>
                    <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                        <div className='w-full flex flex-wrap items-center'>
                            <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="province">Province</label>
                            <select
                                className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                name="province"
                                value={clientData.province ? clientData.province : ""}
                                disabled={getPutPost === 'get'}
                                onChange={updateClient}
                            >
                                <option value="">Select</option>
                                <option value="AB">AB</option>
                                <option value="BC">BC</option>
                                <option value="MB">MB</option>
                                <option value="NB">NB</option>
                                <option value="NL">NL</option>
                                <option value="NT">NL</option>
                                <option value="NS">NS</option>
                                <option value="NU">NU</option>
                                <option value="ON">ON</option>
                                <option value="PE">PE</option>
                                <option value="QC">QC</option>
                                <option value="SK">SK</option>
                                <option value="YT">YT</option>
                            </select>
                            {
                                error && error.province &&
                                <p className='text-red text-xs'>{error.province}</p>

                            }
                        </div>
                    </div>
                    <div className='flex flex-wrap w-full'>
                        <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                            <div className='w-full flex flex-wrap items-center'>
                                <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="postalcode">Postal Code</label>
                                <input
                                    placeholder='Postal Code'
                                    className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                    type="text"
                                    name="postalcode" defaultValue={clientData.postalcode ? clientData.postalcode : ""}
                                    disabled={getPutPost === 'get'}
                                    onChange={updateClient} />
                                {
                                    error && error.postalcode &&
                                    <p className='text-red text-xs'>{error.postalcode}</p>

                                }
                            </div>
                        </div>
                        <div className='input-border flex flex-wrap laptop:w-1/2 tablet:w-full mobile:w-full items-center pt-3 pb-3 mb-6'>
                            <div className='w-full flex flex-wrap items-center'>
                                <div className='w-full flex flex-wrap items-center'>
                                    <label className='mobile:w-1/2 tablet:w-4/12 text-base input-label-color' htmlFor="postalcode">Country</label>
                                    <input
                                        placeholder='Country'
                                        className='mobile:w-1/2 tablet:w-7/12 mr-2 text-base input-color p-2 outline-none hide-input-background'
                                        type="text"
                                        name="country" defaultValue={clientData.country ? clientData.country : ""}
                                        disabled={getPutPost === 'get'}
                                        onChange={updateClient} />
                                    {
                                        error && error.country &&
                                        <p className='text-red text-xs'>{error.country}</p>

                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {
                    ['get'].includes(getPutPost) &&
                    <div className='w-full flex flex-nowrap justify-end text-green4 p-2.5'>
                        <p className='cursor-pointer' onClick={() => setGetPutPost('put')}>
                            Edit Client
                        </p>
                    </div>
                }
                <div className="flex flex-nowrap items-center justify-end w-full">
                    {
                        ['put'].includes(getPutPost) && !loading &&
                        <div className='mr-8 text-green3 cursor-pointer' onClick={() => setGetPutPost('get')}>
                            Cancel
                        </div>
                    }
                    {
                        getPutPost !== 'get' && clientData.client !== "" && !loading && <button className='submit-button' onClick={postClient}>
                            Save
                        </button>
                    }
                </div>
            </div>
        </div>
    );
};

export default Client;